import { Box, Flex, Text } from '@chakra-ui/react';
import { useField, useFormikContext } from 'formik';

import PillSelect from './components/PillSelect';
import { SchemaQuestion, isQuestionVisible, resolveLabel } from './encodeDecodeAnswers';
import MultiSelectToggleCard from './steps/MultiSelectToggleCard';
import StatusRadioCard from './steps/StatusRadioCard';
import TextQuestionCard from './steps/TextQuestionCard';

const BOOL_OPTIONS = [
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

const QuestionError = ({ error, pullUp }: { error: string; pullUp?: boolean }) => (
  <Text fontSize="xs" color="leaseEndRed" mt={pullUp ? -2 : 2} mb={pullUp ? 3 : 0}>
    {error}
  </Text>
);

type PillOption = { value: string; label: string };

type CompactPillRowProps = {
  options: PillOption[];
  value: string;
  onChange: (next: string) => void;
};

const CompactPillRow = ({ options, value, onChange }: CompactPillRowProps) => (
  <Flex gap={2} flexWrap="wrap">
    {options.map((o) => {
      const selected = value === o.value;
      return (
        <Flex
          as="button"
          type="button"
          key={o.value}
          onClick={() => onChange(o.value)}
          px="16px"
          py="8px"
          minH="44px"
          display="inline-flex"
          align="center"
          borderRadius="full"
          border="2px solid"
          borderColor={selected ? 'oceanBoatBlue' : 'gray.200'}
          bg={selected ? 'iceBlue' : 'white'}
          color={selected ? 'oceanBoatBlue' : 'grayLight'}
          fontSize="sm"
          fontWeight={selected ? '600' : '500'}
          cursor="pointer"
          transition="all 0.15s ease"
          _hover={{ borderColor: selected ? 'oceanBoatBlue' : 'lightSkyBlue' }}
        >
          {o.label}
        </Flex>
      );
    })}
  </Flex>
);

type QuestionRendererProps = {
  question: SchemaQuestion;
  goodValue?: string | string[];
  dependents?: SchemaQuestion[];
};

const QuestionRenderer = ({ question, goodValue, dependents = [] }: QuestionRendererProps) => {
  const [field, meta, helpers] = useField(question.questionId);
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const showError = meta.touched && !!meta.error;

  if (question.type === 'text') {
    return <TextQuestionCard question={question} label={question.label} />;
  }

  if (question.type === 'multi_select') {
    return (
      <>
        <MultiSelectToggleCard question={question} />
        {showError && <QuestionError error={meta.error ?? ''} pullUp />}
      </>
    );
  }

  if (question.type === 'enum' && goodValue != null) {
    return (
      <>
        <StatusRadioCard question={question} goodValue={goodValue} />
        {showError && <QuestionError error={meta.error ?? ''} pullUp />}
      </>
    );
  }

  if (question.type === 'enum' && (question.options ?? []).some((o) => o.description)) {
    const handleChange = (v: string) => {
      helpers.setValue(v);
      const next = { ...values, [question.questionId]: v };
      dependents.forEach((d) => {
        if (!isQuestionVisible(d, next)) setFieldValue(d.questionId, '');
      });
    };
    const visibleDependents = dependents.filter((d) => isQuestionVisible(d, values));
    return (
      <Box>
        <PillSelect
          options={question.options ?? []}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={handleChange}
          afterSelected={visibleDependents.map((d) => (
            <TextQuestionCard
              key={d.questionId}
              question={d}
              label={resolveLabel(d, question, values)}
            />
          ))}
        />
        {showError && <QuestionError error={meta.error ?? ''} />}
      </Box>
    );
  }

  let pillOptions: PillOption[] = [];
  let pillValue = '';
  let onPillChange: (next: string) => void = () => undefined;

  if (question.type === 'boolean') {
    pillOptions = BOOL_OPTIONS;
    pillValue = field.value === true ? 'true' : field.value === false ? 'false' : '';
    onPillChange = (v) => helpers.setValue(v === 'true');
  } else if (question.type === 'enum') {
    pillOptions = (question.options ?? []).map((o) => ({ value: o.value, label: o.label }));
    pillValue = typeof field.value === 'string' ? field.value : '';
    onPillChange = (v) => helpers.setValue(v);
  } else {
    return null;
  }

  return (
    <Box mb={5}>
      <Text fontSize="sm" fontWeight="600" color="grayDark" mb={2}>
        {question.label}
      </Text>
      <CompactPillRow options={pillOptions} value={pillValue} onChange={onPillChange} />
      {showError && <QuestionError error={meta.error ?? ''} />}
    </Box>
  );
};

export default QuestionRenderer;
